import React, { useMemo, useState, forwardRef } from "react";
import DataTable, { createTheme } from "react-data-table-component";
import { ChevronUp, ChevronDown, UserCircle } from "lucide-react";
import { useOverallAudience } from "../../../../queries/artistManger/smartLink.query";
import { mapAudienceToRows } from "../LinkHelper";
import { AudienceTableSkeleton } from "../LinkSkeletons";

createTheme("fanbaseTheme", {
  text: { primary: "#00067e", secondary: "#FA6138" },
  background: { default: "#ffffff" },
  context: { background: "#FA6138", text: "#FFFFFF" },
  action: {
    button: "#FA6138",
    hover: "rgba(250,97,56,0.08)",
    disabled: "rgba(0,0,0,.12)"
  }
});
const customStyles = {
  rows: {
    style: {
      cursor: "pointer",
      "&:hover": {
        backgroundColor: "rgba(250,97,56,0.08)",
      },
    },
  },
};

const EmptyAudienceState = () => {
  return (
    <div className="w-full py-16 flex flex-col items-center justify-center text-center">
      <div className="w-14 h-14 mb-4 rounded-full bg-theme_clr2/10 flex items-center justify-center">
        🎵
      </div>

      <h3 className="text-lg font-bold text-theme_clr1 mb-1">
        No fans collected yet
      </h3>

      <p className="text-sm text-theme_clr1/60 max-w-sm">
        When listeners interact with your smart links, their data will start
        appearing here.
      </p>
    </div>
  );
};

export const Fanbase = forwardRef(({ queriesEnabled = true }, ref) => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const { data, isLoading } = useOverallAudience({ page, limit, enabled: queriesEnabled });

  const rows = useMemo(
    () => mapAudienceToRows(data?.items || []),
    [data?.items]
  );


  const columns = [
    {
      name: (
        <div className="font-bold">
          Email / Profile
        </div>
      ),
      selector: row => row.email,
      width: "300px",
      cell: row => (
        <div className="flex items-center gap-2">
          {row.image ? (
            <img
              src={row.image}
              alt="profile"
              className="w-6 h-6 rounded-full object-cover"
            />
          ) : (
            <UserCircle
              size={24}
              className="text-theme_clr1/60"
            />
          )}
          <div className="flex flex-col">
            {row.email.startsWith("http") ? (
              <a
                href={row.email}
                target="_blank"
                rel="noreferrer"
                className="text-theme_clr2 font-bold hover:underline"
              >
                View Profile
              </a>
            ) : (
              <span className="text-theme_clr2 font-bold">{row.email}</span>
            )}
            <span className="text-xs text-theme_clr1/60 font-semibold">{row.username}</span>
          </div>
        </div>
      )
    },
    {
      name: (
        <div className="font-bold">
          Name
        </div>
      ),
      selector: row => (
        <span className="font-semibold">
          {row.name}
        </span>
      ),
      sortable: true
    },
    {
      name:
        (
          <div className="font-bold">
            Location
          </div>
        ),
      selector: row => (
        <span className="font-semibold">
          {row.country}
        </span>
      ),
      sortable: true
    },
    {
      name: (
        <div className="font-bold">
          Added
        </div>
      ),
      selector: row => row.date,
      sortable: true,
      cell: row => (
        <span className="font-semibold capitalize">
          {new Date(row.date).toLocaleDateString()}
        </span>)
    },
    {
      name:
        (
          <div className="font-bold">
            Platform
          </div>
        ),
      selector: row => row.service,
      sortable: true,
      cell: row => (
        <span className="font-semibold capitalize">{row.service}</span>
      )
    }
  ];

  return (
    <div id="tour-fanbase-table">
      <div className="bg-white rounded-lg border border-theme_clr2">
        <DataTable
          columns={columns}
          data={rows}
          theme="fanbaseTheme"
          progressPending={isLoading}
          progressComponent={<AudienceTableSkeleton />}
          noDataComponent={<EmptyAudienceState />}
          pagination={false}
          customStyles={customStyles}
        />
      </div>

      {/* Pagination footer */}
      <div className="flex justify-end items-center p-4 text-sm">
        <div>
          <select
            value={limit}
            onChange={e => {
              setLimit(Number(e.target.value));
              setPage(1);
            }}
            className="w-36 border border-theme_clr1 text-theme_clr2 font-bold rounded-md px-2 py-2 mr-2"
          >
            <option value={10}>10 per page</option>
            <option value={25}>25 per page</option>
            <option value={50}>50 per page</option>
          </select>

          <span className="font-bold text-theme_clr2">
            {data?.total || 0} fans total
          </span>
        </div>

        <div className="flex items-center gap-2 ml-4">
          <button
            disabled={isLoading || page === 1}
            onClick={() => setPage(p => p - 1)}
            className="p-1"
          >
            <ChevronDown
              className={`rotate-90 ${page === 1
                ? "text-theme_clr1/30 cursor-not-allowed"
                : "text-theme_clr2 hover:text-theme_clr2/80"
                }`}
            />
          </button>

          <span className="font-bold text-theme_clr2">
            Page {page} of {Math.ceil((data?.total || 0) / limit)}
          </span>

          <button
            disabled={isLoading || page * limit >= (data?.total || 0)}
            onClick={() => setPage(p => p + 1)}
            className="p-1"
          >
            <ChevronUp
              className={`rotate-90 ${page * limit >= (data?.total || 0)
                ? "text-theme_clr1/30 cursor-not-allowed"
                : "text-theme_clr2 hover:text-theme_clr2/80"
                }`}
            />
          </button>
        </div>
      </div>
    </div>
  );
});
